import { DriveItem } from '@microsoft/microsoft-graph-types';
import { useVariable } from '@softwareimaging/backstage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustomerId } from '../../authentication/hooks/user';
import {
  useGetChildren,
  useGetChildrenRecursive,
  useGetRecentlyModified,
  useGetThumbnail,
  useSearch
} from './onedrive';

const filterFiles = (files: DriveItem[]) => {
  let filteredFiles = files;
  filteredFiles = filteredFiles.filter(file => !file.folder);
  filteredFiles = filteredFiles.filter(file => !file.name?.includes('.sidecar'));

  return filteredFiles;
};
export const useFilteredFiles = (files: DriveItem[]) => {
  const [filteredFiles, setFilteredFiles] = useState<DriveItem[]>(filterFiles(files));

  useEffect(() => {
    setFilteredFiles(filterFiles(files));
  }, [setFilteredFiles, files]);

  return filteredFiles;
};

export const useFilesInFolder = (folderId: string) => {
  const [response, { refetch }] = useGetChildren(folderId);
  const files = useFilteredFiles(response.value);

  files.sort((a, b) => ((a.name?.toLowerCase() || '') > (b.name?.toLowerCase() || '') ? 1 : -1));

  return { files, refetch };
};

export const useFilesInFolderRecursive = (folderId: string) => {
  const [response, { refetch }] = useGetChildrenRecursive(folderId);
  const files = useFilteredFiles(response.value);

  files.sort((a, b) => ((a.name?.toLowerCase() || '') > (b.name?.toLowerCase() || '') ? 1 : -1));

  return { files, refetch };
};

export const useRecentFiles = () => {
  const sharedDirectory = useVariable('sharedResourceFolderId');
  if (!sharedDirectory) {
    throw Error('Could not find shared directory');
  }

  const [response, { refetch }] = useGetRecentlyModified(sharedDirectory);
  const files = useFilteredFiles(response.value);

  return { files, refetch };
};

export const useItemThumbnail = (itemId: string) => {
  const [response, { refetch }] = useGetThumbnail(itemId);

  let thumbnailUrl: string | undefined;
  if (response.value.length > 0 && response.value[0].large?.url) {
    thumbnailUrl = response.value[0].large?.url;
  }

  return { thumbnailUrl, refetch };
};

export const useSearchResults = (searchTerm: string) => {
  const sharedFolderId = useVariable('sharedResourceFolderId');

  if (!sharedFolderId) {
    throw Error('No shared folder ID supplied');
  }

  const myFolderId = useCustomerId();

  if (!sharedFolderId) {
    throw Error('No shared folder ID supplied');
  }

  const [sharedResponse, { refetch: refetchSharedFiles }] = useSearch(searchTerm, sharedFolderId);
  const sharedFiles = useFilteredFiles(sharedResponse.value);

  const [myFilesResponse, { refetch: refetchMyFiles }] = useSearch(searchTerm, myFolderId);
  const myFiles = useFilteredFiles(myFilesResponse.value);

  const refetch = useCallback(() => {
    refetchMyFiles();
    refetchSharedFiles();
  }, [refetchMyFiles, refetchSharedFiles]);

  const combinedResults = useMemo(() => {
    const combinedList = [...myFiles, ...sharedFiles];
    combinedList.sort((a, b) => ((a.name?.toLowerCase() || '') > (b.name?.toLowerCase() || '') ? 1 : -1));

    return combinedList;
  }, [sharedFiles, myFiles]);

  return { files: combinedResults, refetch };
};
